/**
 * Sugestão do saldo de créditos para o distrato (seção 6.1).
 *
 * O campo era digitado à mão e quem gerava copiava da tela de Cancelamentos — que
 * até a correção do saldo mostrava o resultado do mês. O distrato da Ivone Candida
 * Camilo (UC 61215501275, 28/09/2026) saiu com 1.067 kWh quando a conta dizia
 * 4.137,99 kWh. Aqui o sistema sugere o número; quem gera confere e pode editar.
 *
 * SALDO: "SALDO KWH" da conta (`saldo_ugs_vigor`), o estoque de créditos. Usa a
 * conta mais recente QUE TENHA o saldo — a do mês pode ter chegado sem ele
 * (a SET/2026 da Ivone está assim) e "sem saldo" não é o mesmo que "saldo zero".
 *
 * VALOR: saldo × preço da energia com o desconto do cliente (o que ele pagaria à
 * Vigor por kWh), SEM bandeira. Regra deduzida dos distratos já emitidos, em
 * 29/09/2026: dos 3 com valor preenchido, a UC 26538301265 usou R$ 0,9483, que é
 * exatamente esse preço na conta de JUL/2026; a Ivone usou R$ 0,944, a 0,5% do
 * mesmo preço; a UC 10013775152 não tem tarifa gravada para conferir. Amostra
 * pequena — por isso é SUGESTÃO editável, com o cálculo à mostra.
 *
 * Auditoria em TODAS as UCs (29/09/2026, 1.785 UCs, 10.668 contas) — o que mudou:
 *  - O preço vem da conta mais recente que TENHA preço cobrado, não da mesma conta
 *    do saldo. Mês sem injeção (e UC cancelada) chega com preço 0 e sem desconto;
 *    cair na tarifa cheia dava valor ~25% acima em 169 UCs. A Ivone é uma delas:
 *    R$ 0,9515 (tarifa da conta de AGO) → R$ 0,9483 (cobrado em JUL), perto dos
 *    R$ 0,944 que a equipe usou.
 *  - Tarifa cheia × (1 − desconto) só entra se nenhuma conta tiver preço cobrado.
 *  - Preço fora de R$ 0,30–1,50/kWh é tratado como sem preço (6 UCs de contrato
 *    especial tinham R$ 0,05 a 0,32 — o valor sairia irreal).
 *  - Saldo negativo é erro de leitura (a conta nunca imprime saldo negativo): vira
 *    "sem saldo" para conferir, em vez de ir para o documento.
 */

/** Faixa em que um preço por kWh é plausível. Fora dela, o valor fica para preencher à mão. */
export const PRECO_KWH_MIN = 0.3;
export const PRECO_KWH_MAX = 1.5;
const precoPlausivel = (v: number | null): v is number => v !== null && v >= PRECO_KWH_MIN && v <= PRECO_KWH_MAX;

export interface FaturaParaSaldoDistrato {
    uc_codigo_norm: string | null;
    mes_referencia: string | null;
    created_at: string;
    status_fatura?: string | null;
    saldo_ugs_vigor: number | string | null;
    /** Preço cobrado pela Vigor por kWh nesta conta (tarifa com o desconto). */
    tarifa_injecao_gd1?: number | string | null;
    /** Tarifa cheia da distribuidora — base do preço quando o de cima não veio. */
    valor_tarifa_distribuidora?: number | string | null;
    /** Fração: 0.2 = 20%. */
    desconto_tarifa?: number | string | null;
}

export interface SaldoPorUc {
    chave: string;
    kwh: number;
    mesReferencia: string | null;
    /** R$ por kWh usado no valor; null quando a conta não traz tarifa. */
    precoKwh: number | null;
    /**
     * De onde veio o preço. 'tarifa_sem_desconto' = nenhuma conta com preço cobrado
     * e nenhum desconto conhecido: a tela avisa para conferir.
     */
    precoFonte: 'cobrado' | 'tarifa_com_desconto' | 'tarifa_sem_desconto' | null;
}

export interface SugestaoSaldoDistrato {
    /** Soma das UCs com saldo. null quando nenhuma UC tem saldo na conta. */
    kwh: number | null;
    /** null quando alguma UC com saldo não tem preço — melhor em branco que pela metade. */
    valor: number | null;
    porUc: SaldoPorUc[];
    /** UCs sem nenhuma conta com saldo. */
    semSaldo: string[];
}

const num = (v: unknown): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

/** Preço por kWh da conta: o cobrado pela Vigor, ou a tarifa cheia com o desconto. */
export function precoKwhDaConta(f: FaturaParaSaldoDistrato): number | null {
    const cobrado = num(f.tarifa_injecao_gd1);
    if (cobrado !== null && cobrado > 0) return cobrado;
    const cheia = num(f.valor_tarifa_distribuidora);
    if (cheia === null || cheia <= 0) return null;
    const desconto = num(f.desconto_tarifa) ?? 0;
    // desconto_tarifa já veio como 20 (porcentagem) em registros antigos.
    const fracao = desconto > 1 ? desconto / 100 : desconto;
    return cheia * (1 - Math.min(Math.max(fracao, 0), 1));
}

/**
 * `chavesPorUc`: para cada UC do distrato, as chaves `uc_codigo_norm` que casam
 * com ela (número atual e o outro número, se trocou de titular).
 */
export function sugerirSaldoDistrato(
    faturas: FaturaParaSaldoDistrato[],
    chavesPorUc: string[][],
    /** Desconto do cadastro de cada UC (mesma ordem de `chavesPorUc`), para quando nenhuma conta tem preço. */
    descontosCadastro: (number | string | null | undefined)[] = [],
): SugestaoSaldoDistrato {
    const ordenadas = faturas
        .filter(f => String(f.status_fatura ?? '').trim() !== 'Excluído')
        .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));

    const porUc: SaldoPorUc[] = [];
    const semSaldo: string[] = [];
    chavesPorUc.forEach((chaves, i) => {
        if (chaves.length === 0) return;
        const daUc = ordenadas.filter(x => x.uc_codigo_norm && chaves.includes(x.uc_codigo_norm));
        const f = daUc.find(x => num(x.saldo_ugs_vigor) !== null);
        const kwh = f ? num(f.saldo_ugs_vigor)! : null;
        if (!f || kwh === null || kwh < 0) { semSaldo.push(chaves[0]); return; }
        // Preço: o cobrado mais recente e plausível. Sem ele, a tarifa cheia da conta
        // com o desconto — o da conta ou, se ela não gravou, o do cadastro da UC.
        // Prefere o preço que TEM desconto: a conta de SET/2026 de várias UCs veio com o
        // "cobrado" igual à tarifa cheia e sem desconto gravado, enquanto a de AGO
        // tinha os 20% (ex.: UC 157734501274, R$ 1,1458 x R$ 0,9397). Só se nenhuma
        // conta tiver desconto aplicado vale o cobrado como veio.
        const cobrados = daUc
            .map(x => ({ v: num(x.tarifa_injecao_gd1), cheia: num(x.valor_tarifa_distribuidora) }))
            .filter((c): c is { v: number; cheia: number | null } => c.v !== null && c.v > 0 && precoPlausivel(c.v));
        const cobrado = (cobrados.find(c => c.cheia === null || c.v < c.cheia * 0.995) ?? cobrados[0])?.v ?? null;
        if (cobrado !== null) {
            porUc.push({ chave: chaves[0], kwh, mesReferencia: f.mes_referencia, precoKwh: cobrado, precoFonte: 'cobrado' });
            return;
        }
        const descCadastro = num(descontosCadastro[i]);
        const comTarifa = daUc.find(x => (num(x.valor_tarifa_distribuidora) ?? 0) > 0);
        if (!comTarifa) {
            porUc.push({ chave: chaves[0], kwh, mesReferencia: f.mes_referencia, precoKwh: null, precoFonte: null });
            return;
        }
        const descConta = num(comTarifa.desconto_tarifa);
        const desconto = descConta !== null && descConta > 0 ? descConta : (descCadastro !== null && descCadastro > 0 ? descCadastro : null);
        const preco = precoKwhDaConta({ ...comTarifa, tarifa_injecao_gd1: null, desconto_tarifa: desconto ?? 0 });
        porUc.push({
            chave: chaves[0], kwh, mesReferencia: f.mes_referencia,
            precoKwh: precoPlausivel(preco) ? preco : null,
            precoFonte: !precoPlausivel(preco) ? null : desconto !== null ? 'tarifa_com_desconto' : 'tarifa_sem_desconto',
        });
    });

    if (porUc.length === 0) return { kwh: null, valor: null, porUc, semSaldo };
    const kwh = Math.round(porUc.reduce((s, u) => s + u.kwh, 0) * 100) / 100;
    const comSaldo = porUc.filter(u => u.kwh > 0);
    const valor = comSaldo.every(u => u.precoKwh !== null)
        ? Math.round(comSaldo.reduce((s, u) => s + u.kwh * (u.precoKwh as number), 0) * 100) / 100
        : null;
    return { kwh, valor, porUc, semSaldo };
}

/** Formato brasileiro dos campos do distrato: "4.137,99". */
export const fmtNumeroDistrato = (v: number) =>
    v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
